import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import {
  MAT_DIALOG_DATA,
  MatDialogActions,
  MatDialogClose,
  MatDialogContent,
  MatDialogRef,
  MatDialogTitle
} from '@angular/material/dialog';
import { MatButton } from '@angular/material/button';
import { TableType } from '../../../../models/shipment';
import { FormArray, FormControl, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MetadataColumn } from '../../../../models/metadata';
import { NestedPropertyPipe } from '../../pipes/nested-property.pipe';
import { CustomFieldDropdownComponent } from '../../../shipment-details/components/custom-fields/custom-field-dropdown/custom-field-dropdown.component';
import { CustomFieldTextComponent } from '../../../shipment-details/components/custom-fields/custom-field-text/custom-field-text.component';
import { CustomFieldTextareaComponent } from '../../../shipment-details/components/custom-fields/custom-field-textarea/custom-field-textarea.component';
import { CustomFieldDatepickerComponent } from '../../../shipment-details/components/custom-fields/custom-field-datepicker/custom-field-datepicker.component';
import { CustomFieldNumberComponent } from '../../../shipment-details/components/custom-fields/custom-field-number/custom-field-number.component';
import { MatInput } from '@angular/material/input';
import { catchError, NEVER, Subscription, tap } from 'rxjs';
import { UtilsTableService } from '../../services/utils-table.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { UserService } from '../../../../services/user.service';
import { UserType } from '../../../../models/user';
import { Shipment } from '../../../../pages/overview/models/shipment';
import { Container } from '../../../../pages/overview/models/container';

interface DialogData {
  selectedItems: Shipment[] | Container[];
  columns: MetadataColumn[];
  type: TableType;
}

@Component({
  selector: 'app-custom-fields',
  standalone: true,
  templateUrl: './custom-fields.component.html',
  imports: [
    MatDialogTitle,
    MatDialogContent,
    MatDialogActions,
    MatDialogClose,
    MatButton,
    CustomFieldDropdownComponent,
    CustomFieldTextComponent,
    CustomFieldTextareaComponent,
    CustomFieldDatepickerComponent,
    CustomFieldNumberComponent,
    FormsModule,
    MatInput,
    ReactiveFormsModule
  ],
  providers: [NestedPropertyPipe],
  styleUrl: './custom-fields.component.scss'
})
export class CustomFieldsComponent implements OnInit, OnDestroy {
  form = new FormArray<FormGroup>([]);
  saving = false;
  userType: UserType = 'External';

  private subscription = new Subscription();

  constructor(
    @Inject(MAT_DIALOG_DATA)
    public data: DialogData,
    private nestedPropertyPipe: NestedPropertyPipe,
    private utilsTableService: UtilsTableService,
    private matSnackBar: MatSnackBar,
    private matDialogRef: MatDialogRef<CustomFieldsComponent>,
    private userService: UserService
  ) {}

  ngOnInit() {
    this.subscription.add(
      this.userService
        .getUserInfo()
        .pipe(
          tap(({ type }) => {
            this.userType = type;
            this.data.columns.forEach(column => {
              if (this.userType === 'Internal' || column.customFieldType !== 'Internal') {
                const group = this.createGroup(column);
                if (this.groupShouldBeDisabled(group)) {
                  group.disable();
                }
                this.form.push(group);
              }
            });
          })
        )
        .subscribe()
    );
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
  }

  save() {
    this.saving = true;
    const source =
      this.data.type === 'Shipments'
        ? this.utilsTableService.updateShipmentCustomFields(
            (this.data.selectedItems as Shipment[]).map(({ shipmentNo }) => shipmentNo),
            this.mappedFormValues()
          )
        : this.utilsTableService.updateContainerCustomFields(
            (this.data.selectedItems as Container[]).map(({ masterNo, containerNo, customerNo }) => ({
              masterNo,
              containerNo,
              customerNo
            })),
            this.mappedFormValues()
          );
    this.subscription.add(
      source
        .pipe(
          tap(() => {
            this.saving = false;
            this.matSnackBar.open('Custom fields updated successfully.', 'x', { panelClass: 'snackbar-green' });
            this.matDialogRef.close();
          }),
          catchError(error => {
            console.error(error);
            this.saving = false;
            this.matSnackBar.open('Could not update custom fields.', 'x');
            return NEVER;
          })
        )
        .subscribe()
    );
  }

  selectedItemsAsShipments() {
    return this.data.selectedItems as Shipment[];
  }

  selectedItemsAsContainers() {
    return this.data.selectedItems as Container[];
  }

  private createGroup(column: MetadataColumn) {
    const value =
      this.data.selectedItems.length > 1
        ? null
        : (this.nestedPropertyPipe.transform(this.data.selectedItems[0], column.accessPath) ?? null);
    return new FormGroup({
      columnKey: new FormControl(column.key),
      name: new FormControl(column.name),
      dataType: new FormControl(column.dataType || null),
      value: new FormControl(value),
      allowedValues: new FormControl(column.allowedValues),
      validators: new FormControl(column.customFieldValidators),
      customFieldType: new FormControl(column.customFieldType)
    });
  }

  private mappedFormValues() {
    return this.form.getRawValue().reduce((acc, { columnKey, value }) => {
      if (!columnKey) return acc;
      const parts = columnKey.split('.');
      const last = parts[parts.length - 1];
      return { ...acc, [last]: value };
    }, {});
  }

  private groupShouldBeDisabled(group: FormGroup) {
    return group.get('customFieldType')?.value === 'ExternalRead' && this.userType === 'External';
  }
}
